import { z } from "zod"

import { entityIdSchema, tenantIdSchema } from "./identifiers.js"

const currencySchema = z.string().regex(/^[A-Z]{3}$/)
const instantSchema = z.string().datetime({ offset: true })
const optionalInstantSchema = instantSchema.nullable()
const moneySchema = z.number().int().nonnegative()

export const customerPortalBrandV1Schema = z.object({
  title: z.string().min(1).max(80),
  logoUrl: z.string().url().max(2_048).nullable(),
  supportLabel: z.string().min(1).max(80),
  supportHref: z.string().url().max(2_048),
  locale: z.string().regex(/^[a-z]{2,3}(?:-[A-Z]{2})?$/),
  timeZone: z.string().regex(/^[A-Za-z_]+(?:\/[A-Za-z0-9_+-]+)+$/).max(80),
}).strict()

export const customerPortalEstimateActionV1Schema = z.enum([
  "review",
  "request_changes",
  "approve",
  "decline",
  "request_reactivation",
])

export const customerPortalEstimateV1Schema = z.object({
  estimateId: entityIdSchema,
  revisionId: entityIdSchema,
  revisionNumber: z.number().int().positive(),
  aggregateVersion: z.number().int().positive(),
  state: z.enum(["sent", "approved", "declined", "expired", "withdrawn", "superseded"]),
  scopeSummary: z.string().min(1).max(500),
  totalMinor: moneySchema,
  depositMinor: moneySchema,
  currency: currencySchema,
  validUntil: optionalInstantSchema,
  updatedAt: instantSchema,
  actions: z.array(customerPortalEstimateActionV1Schema).max(4),
}).strict().superRefine((estimate, context) => {
  const permitted = new Set(
    estimate.state === "sent"
      ? ["review", "request_changes", "approve", "decline"]
      : estimate.state === "declined" || estimate.state === "expired"
        ? ["review", "request_reactivation"]
        : ["review"]
  )
  if (new Set(estimate.actions).size !== estimate.actions.length) {
    context.addIssue({ code: "custom", path: ["actions"], message: "Actions must be unique." })
  }
  estimate.actions.forEach((action, index) => {
    if (!permitted.has(action)) {
      context.addIssue({
        code: "custom",
        path: ["actions", index],
        message: "Action is not available for the current Estimate state.",
      })
    }
  })
})

export const customerPortalInvoiceActionV1Schema = z.enum(["view", "pay"])

export const customerPortalInvoiceV1Schema = z.object({
  invoiceId: entityIdSchema,
  invoiceNumber: z.string().min(1).max(80),
  recordVersion: z.number().int().positive(),
  state: z.enum(["open", "partially_paid", "paid", "voided"]),
  totalMinor: moneySchema,
  amountPaidMinor: moneySchema,
  balanceDueMinor: moneySchema,
  currency: currencySchema,
  invoiceDate: instantSchema,
  dueDate: z.string().date(),
  updatedAt: instantSchema,
  actions: z.array(customerPortalInvoiceActionV1Schema).min(1).max(2),
}).strict().superRefine((invoice, context) => {
  const expectedBalance = invoice.totalMinor - invoice.amountPaidMinor
  if (expectedBalance !== invoice.balanceDueMinor || expectedBalance < 0) {
    context.addIssue({
      code: "custom",
      path: ["balanceDueMinor"],
      message: "Invoice balance must reconcile to total less paid.",
    })
  }
  const actions = new Set(invoice.actions)
  if (actions.size !== invoice.actions.length || !actions.has("view")) {
    context.addIssue({ code: "custom", path: ["actions"], message: "Invoice actions are invalid." })
  }
  if ((invoice.state === "paid" || invoice.state === "voided") && actions.has("pay")) {
    context.addIssue({
      code: "custom",
      path: ["actions"],
      message: "A terminal Invoice cannot expose a payment action.",
    })
  }
  if ((invoice.state === "open" || invoice.state === "partially_paid") && !actions.has("pay")) {
    context.addIssue({
      code: "custom",
      path: ["actions"],
      message: "A payable Invoice must expose its payment action.",
    })
  }
})

export const customerPortalCommunicationV1Schema = z.object({
  communicationId: entityIdSchema,
  occurredAt: instantSchema,
  channel: z.enum(["email", "sms", "in_app"]),
  subject: z.string().min(1).max(160),
  deliveryState: z.enum(["queued", "sending", "sent", "delivered", "failed", "uncertain"]),
  relatedEntityType: z.enum(["estimate", "invoice", "payment", "portal"]),
  relatedEntityId: entityIdSchema.nullable(),
}).strict()

export const customerPortalPageV1Schema = z.object({
  portalVersion: z.literal(1),
  tenantId: tenantIdSchema,
  customerId: entityIdSchema,
  displayName: z.string().min(1).max(160),
  brand: customerPortalBrandV1Schema,
  balances: z.object({
    openMinor: moneySchema,
    currency: currencySchema,
  }).strict(),
  estimates: z.array(customerPortalEstimateV1Schema).max(100),
  invoices: z.array(customerPortalInvoiceV1Schema).max(100),
  communications: z.array(customerPortalCommunicationV1Schema).max(50),
  communicationNextCursor: z.string().min(1).max(256).nullable(),
}).strict()

export const secureResourceViewV1Schema = z.discriminatedUnion("resourceType", [
  z.object({
    viewVersion: z.literal(1),
    resourceType: z.literal("estimate"),
    tenantId: tenantIdSchema,
    brand: customerPortalBrandV1Schema,
    estimate: customerPortalEstimateV1Schema,
    actionTokenIssued: z.literal(false),
  }).strict(),
  z.object({
    viewVersion: z.literal(1),
    resourceType: z.literal("invoice"),
    tenantId: tenantIdSchema,
    brand: customerPortalBrandV1Schema,
    invoice: customerPortalInvoiceV1Schema,
    actionTokenIssued: z.literal(false),
  }).strict(),
])

export type CustomerPortalPageV1 = z.infer<typeof customerPortalPageV1Schema>
export type SecureResourceViewV1 = z.infer<typeof secureResourceViewV1Schema>
